import React, { useState } from 'react';
import { AlertTriangle, Shield, Phone, Users, Clock, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface TherappiDisclaimerProps {
  isOpen: boolean;
  onAccept: () => void;
  onCancel: () => void;
}

export const TherappiDisclaimer: React.FC<TherappiDisclaimerProps> = ({ 
  isOpen,
  onAccept,
  onCancel
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DisclaimerContent onAccept={onAccept} onCancel={onCancel} />
      </DialogContent>
    </Dialog>
  );
};

const DisclaimerContent: React.FC<{ onAccept: () => void; onCancel: () => void }> = ({ onAccept, onCancel }) => {
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-primary">
          <AlertTriangle className="h-5 w-5" />
          Important Disclaimer - AI Therappi<sup className="text-[0.6em]">®</sup>
        </DialogTitle>
      </DialogHeader>

      <Alert className="border-primary/20 bg-primary/5">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="text-sm font-medium">
          Please read this disclaimer carefully before using AI Therappi<sup className="text-[0.6em]">®</sup>
        </AlertDescription>
      </Alert>

      <div className="space-y-4 text-sm text-muted-foreground">
        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Not a Substitute for Professional Care</p>
            <p>AI Therappi<sup className="text-[0.6em]">®</sup> is a self-guided wellbeing platform that offers tools, resources, and AI-driven support for emotional and psychological wellbeing. It is <strong>not a substitute</strong> for professional mental health care, diagnosis, or treatment by a qualified medical provider.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Phone className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Crisis Support Not Included</p>
            <p>If you are experiencing a <strong>mental health crisis, emergency, or active suicidal thoughts</strong>, please immediately contact a medical professional, emergency services (911), or a crisis hotline in your area. AI Therappi<sup className="text-[0.6em]">®</sup> is not suitable for emergencies.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Clock className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">No Guarantee of Results</p>
            <p>While we provide personalised tools for growth and healing, <strong>results may vary</strong>. Use of this app is at your own discretion and responsibility.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Users className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Age Requirement & Supervision</p>
            <p>You must be <strong>18+ years old</strong> to use AI Therappi<sup className="text-[0.6em]">®</sup>. If under 18, use must be supervised by a parent or legal guardian.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">AI-Powered Technology & Safety Protocols</p>
            <p>THERAPPI<sup className="text-[0.6em]">®</sup> uses advanced AI technology to provide support and guidance. While AI can offer helpful insights, <strong>outputs should not replace professional medical advice</strong>. Always verify AI-generated suggestions independently and consult qualified professionals for medical decisions.</p>
            <p className="mt-2"><strong>AI Will NOT Provide:</strong> Medical diagnoses, nutritional advice, weight loss strategies, support for harmful behaviours (eating disorders, substance abuse, self-harm), medication recommendations, or emergency crisis intervention.</p>
            <p className="mt-2"><strong>Responsible Exploration Mode:</strong> If the AI detects potentially harmful patterns, it will continue to support you reflectively while gently redirecting toward professional care. The AI is programmed to never validate or enable harmful behaviours.</p>
          </div>
        </div>

        <div className="flex items-start gap-3">
          <Shield className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">The Sanctuary: Enhanced Features</p>
            <p>The Sanctuary subscription tier includes access to <strong>personalised timeline integration, condition-specific tools, and advanced reflection modes</strong>. These features use your past timeline events and self-reported mental health data to provide deeper, more tailored support. <strong>Conversation memory is shared</strong> between standard THERAPPI<sup className="text-[0.6em]">®</sup> AI and The Sanctuary, but specialised data (timeline events, condition tracking) is only accessible with an active Sanctuary subscription.</p>
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg border border-primary/20">
          <p className="font-medium text-foreground mb-2">Your Responsibility</p>
          <p>By continuing, you acknowledge that you are responsible for your wellbeing and understand that THERAPPI<sup className="text-[0.6em]">®</sup> is intended for educational and self-development purposes only. You agree to seek professional care where needed.</p>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg border border-primary/20">
          <p className="font-medium text-foreground mb-2">Subscription Tiers & Data Access</p>
          <p className="mb-2">THERAPPI<sup className="text-[0.6em]">®</sup> offers different subscription tiers with varying features:</p>
          <ul className="list-disc pl-6 space-y-1 text-sm">
            <li><strong>Standard THERAPPI<sup className="text-[0.6em]">®</sup> AI:</strong> General reflective support with conversation memory</li>
            <li><strong>The Sanctuary:</strong> Advanced modes, timeline integration, condition-specific tools, and personalised insights based on your past experiences and self-reported conditions</li>
          </ul>
          <p className="mt-2 text-sm"><strong>Important:</strong> If you downgrade from The Sanctuary to standard THERAPPI<sup className="text-[0.6em]">®</sup> AI, your conversation memory will be preserved, but THERAPPI<sup className="text-[0.6em]">®</sup> AI will no longer have access to your timeline events, condition data, or specialised reflection modes. This data remains securely stored and will be accessible again if you resubscribe to The Sanctuary.</p>
        </div>

        <div className="text-xs text-muted-foreground border-t pt-4">
          <p className="mb-2"><strong>Privacy:</strong> All conversations and personal data are confidential, encrypted, and stored securely.</p>
          <p className="mb-2"><strong>Telempathy & Spiritual Content:</strong> Some features include spiritual practices (tarot, oracle cards, psychic development). These are for personal exploration only and not evidence-based medical treatments.</p>
          <p className="mb-2"><strong>Consent Recording:</strong> Your acceptance of this disclaimer will be securely recorded with a timestamp for legal protection purposes.</p>
          <p className="mb-2"><strong>Full Terms:</strong> For complete terms regarding subscriptions, data handling, and user rights, please review our <a href="/terms" className="text-primary hover:underline">Terms & Conditions</a>.</p>
          <p><strong>Questions?</strong> Contact us at <a href="mailto:support@therappi.app" className="text-primary hover:underline">support@therappi.app</a></p>
        </div>
      </div>

      <div className="flex gap-3 pt-4">
        <Button onClick={onCancel} variant="outline" className="flex-1">
          I Do Not Accept
        </Button>
        <Button onClick={onAccept} className="flex-1 bg-primary hover:bg-primary/90">
          I Understand and Accept
        </Button>
      </div>
    </div>
  );
};